import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import heroesData from '../data/heroes.json';
import storiesData from '../data/stories.json';
import MapContainer from '../components/MapContainer';
import './Heroes.css';

interface Hero {
  id: number;
  name: string;
  image: string;
  audio: string;
  description?: string;
}

interface Danmaku {
  id: number;
  content: string;
  created_at: string;
  username?: string;
  class_name?: string;
}

type ViewMode = 'select' | 'story' | 'audio';

const Heroes: React.FC = () => {
  const [heroes, setHeroes] = useState<Hero[]>([]);
  const [selectedHero, setSelectedHero] = useState<Hero | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('select');
  const [danmaku, setDanmaku] = useState<Danmaku[]>([]);
  const [newDanmaku, setNewDanmaku] = useState('');
  const [loading, setLoading] = useState(false);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  useEffect(() => {
    // 从本地JSON文件加载英雄数据
    setHeroes(heroesData as Hero[]);
  }, []);

  useEffect(() => {
    if (selectedHero) {
      fetchDanmaku(selectedHero.id);
      const interval = setInterval(() => {
        fetchDanmaku(selectedHero.id);
      }, 2000); // 每2秒刷新一次弹幕
      return () => clearInterval(interval);
    }
  }, [selectedHero]);

  const fetchDanmaku = async (heroId: number) => {
    try {
      const response = await api.get(`/heroes/${heroId}/danmaku`);
      setDanmaku(response.data);
    } catch (error) {
      console.error('获取弹幕失败:', error);
    }
  };

  const handleHeroClick = (hero: Hero) => {
    setSelectedHero(hero);
    setViewMode('select');
    setNewDanmaku('');
  };

  const handleCloseModal = () => {
    setSelectedHero(null);
    setViewMode('select');
    setDanmaku([]);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const handleViewStory = () => {
    setViewMode('story');
  };

  const handleViewAudio = () => {
    setViewMode('audio');
    if (audioRef.current) {
      audioRef.current.load();
      audioRef.current.play();
    }
  };

  const getStory = (heroId: number): string => {
    return (storiesData as any)[heroId.toString()] || '暂无故事内容';
  };

  const handleSubmitDanmaku = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedHero || !newDanmaku.trim()) return;

    setLoading(true);
    try {
      await api.post(`/heroes/${selectedHero.id}/danmaku`, {
        content: newDanmaku,
      });
      setNewDanmaku('');
      fetchDanmaku(selectedHero.id);
    } catch (error: any) {
      alert(error.response?.data?.message || '发送失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="heroes-container">
      <h1 className="page-title">英雄档案馆</h1>
      <div className="heroes-grid">
        {heroes.map((hero) => (
          <div
            key={hero.id}
            className="hero-card"
            onClick={() => handleHeroClick(hero)}
          >
            <img
              src={hero.image}
              alt={hero.name}
              className="hero-image"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/placeholder-hero.jpg';
              }}
            />
            <h3 className="hero-name">{hero.name}</h3>
          </div>
        ))}
      </div>

      {/* 北海红色足迹地图 */}
      <div className="footprints-section">
        <h2 className="footprints-title">北海红色足迹</h2>
        <div className="footprints-map-container">
          <MapContainer />
        </div>
      </div>

      {selectedHero && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseModal}>
              ×
            </button>
            
            {viewMode === 'select' && (
              <div className="modal-select-mode">
                <h2 className="modal-hero-name">{selectedHero.name}</h2>
                <div className="mode-selection">
                  <button onClick={handleViewStory} className="mode-button mode-story">
                    <span className="mode-icon">📖</span>
                    <span className="mode-text">看故事</span>
                  </button>
                  <button onClick={handleViewAudio} className="mode-button mode-audio">
                    <span className="mode-icon">🎵</span>
                    <span className="mode-text">听语音</span>
                  </button>
                </div>
              </div>
            )}

            {viewMode === 'story' && (
              <div className="modal-hero-info">
                <div className="modal-hero-image-wrapper">
                  <img
                    src={selectedHero.image}
                    alt={selectedHero.name}
                    className="modal-hero-image"
                  />
                  <div className="danmaku-overlay">
                    {danmaku.slice(-20).map((item, index) => {
                      const topPercent = 60 + (index % 6) * 5;
                      const delay = index * 0.5;
                      const duration = 15;
                      
                      return (
                        <div
                          key={item.id}
                          className="danmaku-item"
                          style={{
                            top: `${topPercent}%`,
                            animationDelay: `${delay}s`,
                            animationDuration: `${duration}s`
                          }}
                        >
                          <span className="danmaku-content">{item.content}</span>
                          {(item.class_name || item.username) && (
                            <span className="danmaku-user-info">
                              <span className="danmaku-separator">|</span>
                              {item.class_name && <span className="danmaku-class">{item.class_name}</span>}
                              {item.class_name && item.username && <span className="danmaku-dot">·</span>}
                              {item.username && <span className="danmaku-username">{item.username}</span>}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <h2 className="modal-hero-name">{selectedHero.name}</h2>
                <div className="modal-story-content">
                  <p className="modal-hero-description">{getStory(selectedHero.id)}</p>
                </div>
                <button onClick={() => setViewMode('select')} className="btn btn-secondary">
                  返回选择
                </button>
              </div>
            )}

            {viewMode === 'audio' && (
              <div className="modal-hero-info">
                <div className="modal-hero-image-wrapper">
                  <img
                    src={selectedHero.image}
                    alt={selectedHero.name}
                    className="modal-hero-image"
                  />
                  <div className="danmaku-overlay">
                    {danmaku.slice(-20).map((item, index) => {
                      const topPercent = 60 + (index % 6) * 5;
                      const delay = index * 0.5;
                      const duration = 15;
                      
                      return (
                        <div
                          key={item.id}
                          className="danmaku-item"
                          style={{
                            top: `${topPercent}%`,
                            animationDelay: `${delay}s`,
                            animationDuration: `${duration}s`
                          }}
                        >
                          <span className="danmaku-content">{item.content}</span>
                          {(item.class_name || item.username) && (
                            <span className="danmaku-user-info">
                              <span className="danmaku-separator">|</span>
                              {item.class_name && <span className="danmaku-class">{item.class_name}</span>}
                              {item.class_name && item.username && <span className="danmaku-dot">·</span>}
                              {item.username && <span className="danmaku-username">{item.username}</span>}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <h2 className="modal-hero-name">{selectedHero.name}</h2>
                <audio
                  ref={audioRef}
                  src={selectedHero.audio}
                  controls
                  className="modal-audio"
                />
                <button onClick={() => setViewMode('select')} className="btn btn-secondary">
                  返回选择
                </button>
              </div>
            )}

            {(viewMode === 'story' || viewMode === 'audio') && (
              <div className="danmaku-container">
                <form onSubmit={handleSubmitDanmaku} className="danmaku-form">
                  <input
                    type="text"
                    value={newDanmaku}
                    onChange={(e) => setNewDanmaku(e.target.value)}
                    placeholder="输入留言，如：向江刺横烈士致敬！"
                    className="danmaku-input"
                    maxLength={255}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !newDanmaku.trim()}
                  >
                    发送
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Heroes;

